import { useEffect, useMemo, useState } from "react";
import { getGlpiClient, filterCategoriesForEntity } from "../services/glpiClient";
import { GlpiCategory } from "../types/glpi";

export interface UseCategoriesResult {
  categories: GlpiCategory[];
  loading: boolean;
  error: string | null;
}

// Cache mémoire (durée de vie du task pane).
let _allCategories: GlpiCategory[] | null = null;
let _inFlight: Promise<GlpiCategory[]> | null = null;

function fetchAllCategories(): Promise<GlpiCategory[]> {
  const client = getGlpiClient();
  if (!client) return Promise.resolve([]);
  if (_allCategories) return Promise.resolve(_allCategories);
  if (_inFlight) return _inFlight;

  _inFlight = client.listAllCategories().then((list) => {
    _allCategories = list;
    _inFlight = null;
    return list;
  });
  return _inFlight;
}

/**
 * Charge l'ensemble des catégories ITIL une seule fois (cache mémoire) puis
 * applique un filtre par entité côté client (même comportement que l'UI GLPI :
 * une catégorie appartient à son entité OU est récursive donc héritée).
 */
export function useCategories(entityId: number | null): UseCategoriesResult {
  const [all, setAll] = useState<GlpiCategory[]>(_allCategories ?? []);
  const [loading, setLoading] = useState(_allCategories === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (_allCategories) {
      setAll(_allCategories);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAllCategories()
      .then((data) => {
        if (!cancelled) setAll(data);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    if (entityId === null || entityId === undefined) return [];
    return filterCategoriesForEntity(all, entityId);
  }, [all, entityId]);

  return { categories, loading, error };
}
